/**
 * onDoneFallbackDecision.ts — BUG-DR-DONE-FALLBACK (2026-05-21,
 * follow-up to PR #198, Slack C0AM8U804KZ/1778281880.499349 message
 * 1779363662.984699).
 *
 * Pure decision helper extracted from `useGlobalTools.ts`'s prest0n-
 * session SSE `onDone` callback so the four-branch fallback contract is
 * testable in isolation (no React, no hook, no jest-dom).
 *
 * Context — the bug this fix neutralizes:
 *   PR #198 (2026-05-20) made the orchestrator planner correctly emit
 *   an empty `finalResponse` when invoking surface-owning tools like
 *   the deep-research clarifying-questions card or the full-pipeline
 *   Deep Research Report. That suppression is correct on the backend.
 *
 *   The frontend `onDone` safety-net in useGlobalTools.ts was emitting
 *   a bare `'Done.'` system message (sourceTag
 *   `prest0n_session_done_empty`) whenever `handled=true` AND no
 *   streaming text had been finalized. That stray bubble:
 *     1. visually duplicates the surface the tool already painted, AND
 *     2. (the actual P0 demo-blocker) inserts itself between the
 *        clarifying card and the user's `'skip'` reply, displacing the
 *        most-recent prior assistant turn that
 *        `findImmediatelyPriorQualificationIndex` in
 *        midClarifyingFreeTextPreRoute.ts walks back to find. With the
 *        qualification card no longer immediately-prior, the pre-route
 *        returns null and the planner falls through to a generic ack
 *        ("Entendido. ¿En qué puedo ayudarte?") instead of dispatching
 *        deep-research with skipClarification=true.
 *
 * Decision contract — given the no-streaming-text fallback path:
 *   (a) handled=true, surfaceOwnedByTool=true   → `{ kind: 'suppress' }`
 *       The backend already painted the user-visible surface (clarifying
 *       card, DR Report, etc.). Emitting `'Done.'` underneath is a
 *       cosmetic regression AND breaks the skip-resume pre-route.
 *   (b) handled=true, surfaceOwnedByTool=false  → emit `'Done.'`
 *       Defensive backward-compat for legacy deploys / non-surface-
 *       owning tools that legitimately finished with no prose.
 *   (c) handled=true, surfaceOwnedByTool=undefined → emit `'Done.'`
 *       Old backend (pre-A-layer) doesn't send the flag at all; default
 *       to the legacy behavior so older sessions aren't silenced.
 *   (d) handled=false → emit "I finished processing…"
 *       Unchanged from PR #198 / pre-existing behavior. handled=false
 *       means the SSE done event carried no positive completion signal
 *       at all; the user needs to know to retry.
 *
 * The `hasAccumulatedText` input pins that the suppression only applies
 * when there is genuinely NO planner prose to surface. If accumulated
 * text exists, the streaming-message branch in useGlobalTools.ts owns
 * the finalize, and this helper is not called.
 */

import {
  SEND_FAILURE_RETRY_SOURCE_TAG,
  SEND_FAILURE_EMPTY_REPLY_TEXT,
} from '../../../components/GlobalChatSystem/sendFailureRetry';

export type OnDoneFallbackDecision =
  | { kind: 'suppress' }
  | { kind: 'system'; text: string; sourceTag: string }
  // Round J (2026-07-14 empty-turn guard): the terminal produced ZERO
  // assistant content and no owned surface — the caller must render the
  // retryable error bubble (interrupted + regeneratePrompt), never a bare
  // 'Done.' the user can't act on. This is the client-side catch for old
  // deployed servers that still emit a bare done:{handled:true} on an
  // empty VM stream, and for any other empty terminal.
  | { kind: 'retryable_error'; text: string; sourceTag: string };

/**
 * Decide what the `onDone` callback should do when there is no in-
 * flight streaming text message to finalize. Pure / synchronous / no
 * side effects — safe to call on every SSE `done` event.
 */
export function decideOnDoneFallback(
  handled: boolean,
  surfaceOwnedByTool: boolean | undefined,
  hasAccumulatedText: boolean,
  surfaceActuallyPainted: boolean = surfaceOwnedByTool === true,
): OnDoneFallbackDecision {
  if (handled && surfaceOwnedByTool === true && surfaceActuallyPainted && !hasAccumulatedText) {
    return { kind: 'suppress' };
  }
  if (!hasAccumulatedText) {
    // Empty terminal with no owned surface — handled or not, the user got
    // nothing visible. Surface the retryable bubble (Round J).
    return {
      kind: 'retryable_error',
      // The turn ran and came back empty — say that, not "couldn't start"
      // (2026-09-02: the misnamed class hid the real failure from triage).
      text: SEND_FAILURE_EMPTY_REPLY_TEXT,
      sourceTag: SEND_FAILURE_RETRY_SOURCE_TAG,
    };
  }
  // Defensive branch: accumulated text exists but no streaming message was
  // registered (the streaming branch normally owns this). Keep the legacy
  // ack so a text-bearing turn is never mislabeled as a failure.
  return {
    kind: 'system',
    text: handled
      ? 'Done.'
      : 'I finished processing, but no response text was returned. Please try again.',
    sourceTag: handled
      ? 'prest0n_session_done_empty'
      : 'prest0n_session_empty_response',
  };
}
